/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { isSameEntity, sortUtteranceEntities } from 'src/luis/projects/testPane/services/endpointPredictionMapper/predictionMapperHelpers';
import {
    EndpointUtteranceResult,
    UtteranceCompositeEntityStore,
    UtteranceEntityWithTextStore,
    UtteranceResultStore
} from 'src/luis/projects/testPane/types/types';
import { EntityLabel } from 'src/luis/projects/utterances/types/Utterance';
import { ModelType } from 'src/luis/types/ModelType';

/**
 * Extracts the predictions for entities and composite entities for an utterance
 * and its multi intent sub predictions from the trained model prediction api.
 * Returns an array of utterance results, where the first utterance in the array
 * is the main utterance and the rest of the array are the predicted multi intent
 * sub utterances.
 *
 * @param utterance The utterance to get results for.
 * @param entities The entities of the current application.
 * @returns An array of utterance result objects where the first data point in the array
 * is the main utterance and the rest are multi intent sub predictions.
 */
export const getUtteranceTrainedPredictions = (
    utterance: EndpointUtteranceResult,
    entities: Entity[],
    disabledUtterance: EndpointUtteranceResult
): UtteranceResultStore => {
    // Build the hierarchy of the response in case of disabled required features.
    const disabledNDepth = _getNDepthEntitiesFromPredictionApi(
        disabledUtterance.predictedEntities,
        entities,
        disabledUtterance.tokenizedText
    );

    /**
     * Compare the enabled/disabled hierarchies to define which
     * trees/sub-trees that belongs to the disabled state only to
     * be highlighted.
     */
    const nDepths = determineExtraEntitiesFromEnablingConstraints(
        // Build the hierarchy of the response in case of enabled required features.
        _getNDepthEntitiesFromPredictionApi(utterance.predictedEntities, entities, utterance.tokenizedText),
        disabledNDepth
    );

    return {
        mainUtterance: {
            nDepths,
            utterance,
            entities: _getEntitiesFromPredictionApi(utterance, entities),
            composites: _getCompositeEntitiesFromPredictionApi(utterance.predictedEntities, entities, utterance.tokenizedText)
        }
    };
};

/**
 * Gets the entities from the prediction api and matches them with their parent composite entities
 * by inspecting their schema from the original entities array of the app.
 *
 * So for each entity predicted in the utterance:
 * - Checks if it is a child of any of the composite entities found in the predictions for this utterance.
 * - If it was found, it is matched with that composite entity.
 * - The corresponding token text for that entity is matched with the entity name.
 *
 * @param utterance The utterance that was predicted.
 * @param entities The entities in the LUIS app.
 * @returns An array of utterance entities with their matching token text. Ex: "My name is Omar"
 * would return { entity: "firstName", text: "Omar" }.
 */
const _getEntitiesFromPredictionApi = (utterance: EndpointUtteranceResult, entities: Entity[]): UtteranceEntityWithTextStore[] => {
    const composites = _getCompositeEntitiesFromPredictionApi(utterance.predictedEntities, entities, utterance.tokenizedText);
    const parentsAndChildren: UtteranceEntityWithTextStore[] = composites.reduce((a, cE) => a.concat(cE.parent, ...cE.children), []);

    // Throw away simple entities without roles and the composite entities and returns the other types of entities.
    return utterance.predictedEntities
        .filter(e => e.entity.typeId !== ModelType.Simple)
        .filter(
            predictedEntity =>
                !parentsAndChildren.find(e =>
                    isSameEntity(e, {
                        isSuppressed: false,
                        utteranceEntity: predictedEntity,
                        text: utterance.tokenizedText.slice(predictedEntity.startTokenIndex, predictedEntity.endTokenIndex + 1).join(' ')
                    })
                )
        )
        .map(uE => ({
            utteranceEntity: uE,
            isSuppressed: false,
            text: utterance.tokenizedText.slice(uE.startTokenIndex, uE.endTokenIndex + 1).join(' ')
        }))
        .sort((a, b) => sortUtteranceEntities(a.utteranceEntity, b.utteranceEntity, false));
};

/**
 * Given the entities predicted, matched with the app entities, and tokenized
 * text, outputs an array of composite entities with their children, and for
 * each parent and child, the text segment it covers.
 *
 * @param utteranceEntities The predicted entities in the utterance.
 * @param entities All the entities in the application
 * @param tokens The tokenized text of the utterance.
 * @returns An array of composite entities with their text and children.
 */
const _getCompositeEntitiesFromPredictionApi = (
    utteranceEntities: EntityLabel[],
    entities: Entity[],
    tokens: string[]
): UtteranceCompositeEntityStore[] => {
    // missing supporting hierarchical entities
    const expandedEntities: Entity[] = entities.reduce((acc, e) => acc.concat([e, ...e.roles]), []);
    const inApp = utteranceEntities.filter(uE => expandedEntities.some(e => e.id === uE.entity.id));
    const composites = inApp.filter(uE => uE.entity.typeId === ModelType.Composite);
    const others = inApp.filter(uE => uE.entity.typeId !== ModelType.Composite);
    const compositeEntities = entities.filter(e => e.typeId === ModelType.Composite);

    return composites
        .map(compositeUtteranceEntity => {
            const parentData: UtteranceEntityWithTextStore = {
                isSuppressed: false,
                utteranceEntity: compositeUtteranceEntity,
                text: tokens.slice(compositeUtteranceEntity.startTokenIndex, compositeUtteranceEntity.endTokenIndex + 1).join(' ')
            };
            const utteranceCompositeEntityStore: UtteranceCompositeEntityStore = { parent: parentData, children: [] };
            const childrenEntities = (<CompositeEntity[]>compositeEntities).find(c => c.id === compositeUtteranceEntity.entity.id).children;

            utteranceCompositeEntityStore.children = others
                .filter(uE =>
                    childrenEntities.find(
                        c =>
                            (c.id === uE.entity.id || c.id === uE.role.id) &&
                            compositeUtteranceEntity.startTokenIndex <= uE.startTokenIndex &&
                            uE.endTokenIndex <= compositeUtteranceEntity.endTokenIndex
                    )
                )
                .map(uE => ({
                    parent: {
                        utteranceEntity: uE,
                        isSuppressed: false,
                        text: tokens.slice(uE.startTokenIndex, uE.endTokenIndex + 1).join(' ')
                    },
                    // This array is empty because the composite entity has only 1 level deep of children.
                    children: []
                }));

            return utteranceCompositeEntityStore;
        })
        .sort((a, b) => sortUtteranceEntities(a.parent.utteranceEntity, b.parent.utteranceEntity, false));
};

/**
 * Given the entities predicted, matched with the app entities, and tokenized
 * text, outputs an array of n-depth entities with their children, and for
 * each parent and child, the text segment it covers.
 *
 * @param utteranceEntities The predicted entities in the utterance.
 * @param entities All the entities in the application
 * @param tokens The tokenized text of the utterance.
 * @returns An array of n-depth entities with their text and children.
 */
export const _getNDepthEntitiesFromPredictionApi = (
    utteranceEntities: EntityLabel[],
    entities: Entity[],
    tokens: string[]
): UtteranceCompositeEntityStore[] => {
    const expandedEntities: Entity[] = entities.reduce(
        (acc, e) =>
            acc.concat(
                e.typeId === ModelType.Simple
                    ? [...e.roles, ...flattenNDepthEntity([<SimpleEntity>e])]
                    : [e, ...e.roles, ...((<SimpleEntity>e).children ? (<SimpleEntity>e).children : [])]
            ),
        []
    );
    const utteranceSimpleEntities = utteranceEntities.filter(uE => uE.entity.typeId === ModelType.Simple);
    const utteranceNonNDepthType = utteranceEntities.filter(
        uE =>
            uE.entity.typeId === ModelType.Simple ||
            uE.entity.typeId === ModelType.List ||
            uE.entity.typeId === ModelType.Prebuilt ||
            uE.entity.typeId === ModelType.Regex
    );

    return utteranceSimpleEntities
        .map(utteranceSimpleEntity => {
            const parentData: UtteranceEntityWithTextStore = {
                isSuppressed: false,
                utteranceEntity: utteranceSimpleEntity,
                text: tokens.slice(utteranceSimpleEntity.startTokenIndex, utteranceSimpleEntity.endTokenIndex + 1).join(' ')
            };
            const utteranceCompositeEntityStore: UtteranceCompositeEntityStore = { parent: parentData, children: [] };

            utteranceCompositeEntityStore.children = _getNDepthChildren(
                utteranceNonNDepthType,
                <SimpleEntity[]>expandedEntities,
                utteranceSimpleEntity,
                tokens
            );

            return utteranceCompositeEntityStore;
        })
        .filter(u => entities.find(e => e.id === u.parent.utteranceEntity.entity.id))
        .sort((a, b) => sortUtteranceEntities(a.parent.utteranceEntity, b.parent.utteranceEntity, false));
};

/**
 * Calculates the children for the n-depth entity from
 * the predicted entities to build the whole predicted
 * tree.
 *
 * @param utteranceNonNDepthType The non n-depth type predicted entities.
 * @param expandedEntities The flatten entities in the application.
 * @param parent the entity to calculate the children for.
 * @param tokens Array of the tokens of th utterance text.
 */
const _getNDepthChildren = (
    utteranceNonNDepthType: EntityLabel[],
    expandedEntities: SimpleEntity[],
    parent: EntityLabel,
    tokens: string[]
): UtteranceCompositeEntityStore[] => {
    const getEntity = expandedEntities.find(c => c.id === parent.entity.id);
    const childrenEntities = getEntity.children ? getEntity.children : [];

    return utteranceNonNDepthType
        .filter(uE =>
            childrenEntities.find(
                c => c.id === uE.entity.id && parent.startTokenIndex <= uE.startTokenIndex && uE.endTokenIndex <= parent.endTokenIndex
            )
        )
        .map(uE => ({
            parent: {
                utteranceEntity: uE,
                isSuppressed: false,
                text: tokens.slice(uE.startTokenIndex, uE.endTokenIndex + 1).join(' ')
            },
            children: _getNDepthChildren(utteranceNonNDepthType, expandedEntities, uE, tokens)
        }))
        .sort((a, b) => sortUtteranceEntities(a.parent.utteranceEntity, b.parent.utteranceEntity, false));
};

/**
 * Flatten the n-depth entity nested children in 1-D array
 *
 * @param parents The n-depth entity parent to parse
 * @param results The result array of flattened children
 */
export const flattenNDepthEntity = (parents: SimpleEntity[], results?: (Entity | SimpleEntity)[]) => {
    if (!results) {
        results = [];
    }

    parents.forEach(parent => {
        results.push({
            id: parent.id,
            name: parent.name,
            roles: [],
            typeId: parent.typeId,
            features: [],
            children: parent.children,
            labelsCount: parent.labelsCount
        });
        if (parent.instanceOf) {
            results.push(parent.instanceOf);
        }
        if (parent.children.length) {
            flattenNDepthEntity(parent.children, results);
        }
    });

    return results;
};

/**
 * Determines the extra entities in the entitiesWithoutConstraints
 * (the case of disabled required features) that do not exist in
 * entitiesWithConstraint (the case of enabled required features)
 * to be highlighted.
 *
 * @param entitiesWithConstraint The predicted entities with enabled required features.
 * @param entitiesWithoutConstraints The predicted entities with disabled required features.
 */
const determineExtraEntitiesFromEnablingConstraints = (
    entitiesWithConstraint: UtteranceCompositeEntityStore[],
    entitiesWithoutConstraints: UtteranceCompositeEntityStore[]
): UtteranceCompositeEntityStore[] => {
    // Check first level (roots level).
    const newPredictedEntities = entitiesWithConstraint.concat(
        checkFiringWithConstrains(entitiesWithConstraint, entitiesWithoutConstraints)
    );

    // Go deep for children.
    newPredictedEntities.forEach(nDepthEntity =>
        determineExtraEntitiesFromEnablingConstraintsRec(nDepthEntity, entitiesWithoutConstraints)
    );

    return newPredictedEntities;
};

/**
 * The recursive method that applies the distinguishing between entities
 * from enabled/disabled required features predictions.
 *
 * @param entity The predicted entity to be modified.
 * @param entitiesWithoutConstraints The same-level predicted entities from
 * the disabled required features prediction.
 */
const determineExtraEntitiesFromEnablingConstraintsRec = (
    entity: UtteranceCompositeEntityStore,
    entitiesWithoutConstraints: UtteranceCompositeEntityStore[]
) => {
    /**
     * This recursion should stop if the entity does not have children or
     * there is no equal-level entities in the disabled required features
     * prediction.
     */
    if (!entity.children.length || !entitiesWithoutConstraints.length) {
        return;
    }

    // Get the entity from the disabled required features prediction if exists.
    const entityFromDisabledConstraintsPrediction = entitiesWithoutConstraints.find(
        e =>
            e.parent.utteranceEntity.entity.id === entity.parent.utteranceEntity.entity.id &&
            e.parent.utteranceEntity.startTokenIndex === entity.parent.utteranceEntity.startTokenIndex &&
            e.parent.utteranceEntity.endTokenIndex === entity.parent.utteranceEntity.endTokenIndex
    );

    entity.children.forEach(child =>
        determineExtraEntitiesFromEnablingConstraintsRec(child, entityFromDisabledConstraintsPrediction.children)
    );

    if (entityFromDisabledConstraintsPrediction) {
        entity.children = entity.children.concat(
            checkFiringWithConstrains(entity.children, entityFromDisabledConstraintsPrediction.children)
        );
    }
};

/**
 * Checks for the extra predicted entities in the disabled
 * required features prediction.
 *
 * @param entitiesWithConstraint The predicted entities in the
 * enabled required features prediction.
 * @param entitiesWithoutConstraints The predicted entities in the
 * disabled required features prediction.
 */
const checkFiringWithConstrains = (
    entitiesWithConstraint: UtteranceCompositeEntityStore[],
    entitiesWithoutConstraints: UtteranceCompositeEntityStore[]
): UtteranceCompositeEntityStore[] =>
    entitiesWithoutConstraints
        .filter(
            nDepthEntity =>
                !entitiesWithConstraint.some(
                    entity =>
                        entity.parent.utteranceEntity.entity.id === nDepthEntity.parent.utteranceEntity.entity.id &&
                        entity.parent.utteranceEntity.startTokenIndex === nDepthEntity.parent.utteranceEntity.startTokenIndex &&
                        entity.parent.utteranceEntity.endTokenIndex === nDepthEntity.parent.utteranceEntity.endTokenIndex
                )
        )
        .map(markAsFiredWithDisabledConstrains);

/**
 * Marks the given entity recursively with its children as
 * extra entity for the disabled required features prediction.
 */
const markAsFiredWithDisabledConstrains = (entity: UtteranceCompositeEntityStore): UtteranceCompositeEntityStore => {
    entity.parent.isSuppressed = true;

    entity.children.forEach(markAsFiredWithDisabledConstrains);

    return entity;
};
